import clsx from 'clsx';
import { ChevronRightIcon, ListBulletIcon, UsersIcon } from '@heroicons/react/24/outline';
import type { Poll } from '@flock-types/polls.types';

export function getTimeRemaining(closesAt?: string): string {
  if (!closesAt) return 'No end date';
  const diffMs = new Date(closesAt).getTime() - Date.now();
  if (diffMs <= 0) return 'Ended';

  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays > 0) return `Closes in ${diffDays}d`;
  if (diffHours > 0) return `Closes in ${diffHours}h`;
  return `Closes in ${diffMins}m`;
}

interface Props {
  poll: Poll;
  onOpen: (poll: Poll) => void;
}

/** Poll row in the list pane (port of the Expo polls/index card). */
export default function PollCard({ poll, onOpen }: Props) {
  const isArchived = poll.status === 'archived';
  const isClosed = poll.status === 'closed' || isArchived;
  const timeText = isArchived
    ? 'Archived'
    : poll.status === 'closed'
      ? 'Closed'
      : getTimeRemaining(poll.closes_at);

  return (
    <button
      onClick={() => onOpen(poll)}
      className="flex w-full items-center gap-3 rounded-xl border border-flock-gray-200 bg-white p-4 text-left transition hover:border-flock-navy-300 hover:shadow-sm"
    >
      <div className="min-w-0 flex-1">
        <div className="line-clamp-2 text-base font-semibold text-flock-navy-900">
          {poll.question}
        </div>
        <div className="mt-1 flex items-center gap-2 text-sm text-flock-navy-500">
          <span className="flex items-center gap-1">
            <ListBulletIcon className="h-4 w-4" />
            {poll.option_count ?? 0} options
          </span>
          <span className="text-flock-navy-300">·</span>
          <span className="flex items-center gap-1">
            <UsersIcon className="h-4 w-4" />
            {poll.total_voters ?? 0} voted
          </span>
        </div>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1">
        <span
          className={clsx(
            'rounded-full px-2 py-1 text-xs font-medium',
            isClosed ? 'bg-flock-gray-200 text-flock-navy-500' : 'bg-green-100 text-green-700'
          )}
        >
          {isClosed ? 'Closed' : 'Active'}
        </span>
        <span className="text-xs text-flock-navy-400">{timeText}</span>
      </div>
      <ChevronRightIcon className="h-5 w-5 shrink-0 text-flock-navy-300" />
    </button>
  );
}
